/**
 * Modern Welcome Page for go.liantiaoja.com (Pilot Site)
 * Professional theme with Jamaica colors
 * Features: Service cards, price calculator, PUDO finder, Tracking, Auth
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import LaunchPromoBanner from "../../components/LaunchPromoBanner";
import {
  Car,
  Plane,
  Package,
  Sofa,
  Truck,
  MapPin,
  QrCode,
  CreditCard,
  Shield,
  ChevronRight,
  Navigation,
  Zap,
  Clock,
  Users,
  Star,
  Check,
  ArrowRight,
  Search,
  LocateFixed,
  Loader2,
  Phone,
  ExternalLink,
  User,
  LogIn,
  Eye,
  MapPinned,
  Map
} from "lucide-react";
import { toast } from "sonner";

// Logo path
const LOGO_PATH = "/assets/liantiao-logo.png";

// Professional theme (fixed - no switcher)
const theme = {
  bg: "bg-gradient-to-b from-white to-slate-50",
  banner: "bg-emerald-900 text-white",
  header: "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm",
  headerText: "text-slate-800",
  logo: "text-emerald-900",
  hero: "bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900",
  heroText: "text-white",
  heroAccent: "text-amber-400",
  card: "bg-white border border-slate-200 shadow-md hover:shadow-lg hover:-translate-y-1",
  cardTitle: "text-slate-900",
  cardText: "text-slate-500",
  cardIcon: "bg-emerald-900 text-white",
  btnPrimary: "bg-amber-400 text-emerald-900 hover:bg-amber-300 font-semibold",
  btnSecondary: "bg-emerald-900 text-white hover:bg-emerald-800",
  section: "bg-white",
  sectionAlt: "bg-slate-50",
  accent: "text-emerald-800",
  accentBg: "bg-emerald-900",
  gold: "text-amber-500",
  goldBg: "bg-amber-400",
  badge: "bg-amber-400 text-emerald-900",
  footer: "bg-emerald-950 text-white"
};

const ModernWelcome = () => {
  const navigate = useNavigate();
  const [priceEstimate, setPriceEstimate] = useState(800);
  const [priceWeight, setPriceWeight] = useState("2");
  const [priceDistance, setPriceDistance] = useState("5");
  const [animatedStats, setAnimatedStats] = useState({ deliveries: 0, drivers: 0, pudos: 0 });
  
  // Jamaica Parishes
  const PARISHES = [
    "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary", 
    "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
    "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine"
  ];
  
  // Structured Address State for Pickup (Sender)
  const [pickupAddress, setPickupAddress] = useState({
    streetNumber: '',
    streetName: '',
    town: '',
    parish: '',
    lat: null,
    lng: null,
    verified: false
  });
  
  // Structured Address State for Dropoff (Receiver)
  const [dropoffAddress, setDropoffAddress] = useState({
    streetNumber: '',
    streetName: '',
    town: '',
    parish: '',
    lat: null,
    lng: null,
    verified: false
  });
  
  // GPS Loading states
  const [gettingPickupGPS, setGettingPickupGPS] = useState(false);
  const [gettingDropoffGPS, setGettingDropoffGPS] = useState(false);
  const [calculatedDistance, setCalculatedDistance] = useState(null);
  
  // Quote ready state
  const [quoteReady, setQuoteReady] = useState(false);
  const [showBookingPrompt, setShowBookingPrompt] = useState(false);
  
  // Legacy state for compatibility
  const [quotePickup, setQuotePickup] = useState({ lat: null, lng: null, address: '' });
  const [quoteDropoff, setQuoteDropoff] = useState({ lat: null, lng: null, address: '' });
  
  // Address Search State (for dropoff autocomplete)
  const [dropoffSearchResults, setDropoffSearchResults] = useState([]);
  const [searchingDropoff, setSearchingDropoff] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  
  // Map Picker Modal State
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapSearchResults, setMapSearchResults] = useState([]);
  const [searchingMap, setSearchingMap] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [selectedMapLocation, setSelectedMapLocation] = useState(null);
  const [reverseGeocodingMap, setReverseGeocodingMap] = useState(false);
  
  // Tracking State
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  
  // PUDO Search State
  const [pudoSearchQuery, setPudoSearchQuery] = useState("");
  const [pudoLocations, setPudoLocations] = useState([]);
  const [pudoLoading, setPudoLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locatingUser, setLocatingUser] = useState(false);
  const [showAllPudos, setShowAllPudos] = useState(false);

  // Haversine distance calculation
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Update price when GPS locations change
  useEffect(() => {
    // Check both legacy and new structured addresses
    const hasPickup = pickupAddress.lat || quotePickup.lat;
    const hasDropoff = dropoffAddress.lat || quoteDropoff.lat;
    
    if (hasPickup && hasDropoff) {
      const pickLat = pickupAddress.lat || quotePickup.lat;
      const pickLng = pickupAddress.lng || quotePickup.lng;
      const dropLat = dropoffAddress.lat || quoteDropoff.lat;
      const dropLng = dropoffAddress.lng || quoteDropoff.lng;
      
      const dist = calculateDistance(pickLat, pickLng, dropLat, dropLng);
      setCalculatedDistance(dist);
      // Update dropdown to match calculated distance
      if (dist <= 5) setPriceDistance("5");
      else if (dist <= 10) setPriceDistance("10");
      else if (dist <= 20) setPriceDistance("20");
      else setPriceDistance("30");
    }
  }, [pickupAddress.lat, dropoffAddress.lat, quotePickup.lat, quoteDropoff.lat]);

  // Get GPS location for quote calculator
  const getQuoteGPS = async (type) => {
    const setGetting = type === 'pickup' ? setGettingPickupGPS : setGettingDropoffGPS;
    const setLocation = type === 'pickup' ? setQuotePickup : setQuoteDropoff;
    
    setGetting(true);
    
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      setGetting(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        
        // Validate Jamaica bounds
        if (latitude < 17.5 || latitude > 18.7 || longitude < -78.5 || longitude > -76.0) {
          toast.error("Location must be within Jamaica");
          setGetting(false);
          return;
        }
        
        // Reverse geocode
        try {
          const response = await fetch(`/api/geocode/reverse?lat=${latitude}&lng=${longitude}`);
          if (response.ok) {
            const data = await response.json();
            setLocation({ lat: latitude, lng: longitude, address: data.address || 'Location captured' });
            toast.success(`${type === 'pickup' ? 'Pickup' : 'Dropoff'} location set!`);
          } else {
            setLocation({ lat: latitude, lng: longitude, address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` });
          }
        } catch {
          setLocation({ lat: latitude, lng: longitude, address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` });
        }
        setGetting(false);
      },
      (err) => {
        if (err.code === 1) toast.error("Location permission denied. Please enable in browser settings.");
        else if (err.code === 2) toast.error("Location unavailable. Try again.");
        else toast.error("Location request timed out. Try again.");
        setGetting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Enhanced GPS function that parses address into components
  const getStructuredGPS = async (type) => {
    const setGetting = type === 'pickup' ? setGettingPickupGPS : setGettingDropoffGPS;
    const setAddress = type === 'pickup' ? setPickupAddress : setDropoffAddress;
    const setLegacy = type === 'pickup' ? setQuotePickup : setQuoteDropoff;
    
    setGetting(true);
    
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      setGetting(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        
        // Validate Jamaica bounds
        if (latitude < 17.5 || latitude > 18.7 || longitude < -78.5 || longitude > -76.0) {
          toast.error("Location must be within Jamaica");
          setGetting(false);
          return;
        }
        
        // Reverse geocode with detailed address parsing
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          
          if (response.ok) {
            const data = await response.json();
            const addr = data.address || {};
            
            // Parse parish from various fields
            let parish = '';
            const parishFields = [addr.county, addr.state_district, addr.suburb, addr.city_district];
            for (const field of parishFields) {
              if (field) {
                const foundParish = PARISHES.find(p => 
                  field.toLowerCase().includes(p.toLowerCase()) ||
                  p.toLowerCase().includes(field.toLowerCase().replace('parish', '').trim())
                );
                if (foundParish) {
                  parish = foundParish;
                  break;
                }
              }
            }
            
            // Try to match from display name if not found
            if (!parish && data.display_name) {
              for (const p of PARISHES) {
                if (data.display_name.includes(p)) {
                  parish = p;
                  break;
                }
              }
            }
            
            const streetNumber = addr.house_number || '';
            const streetName = addr.road || addr.street || '';
            const town = addr.town || addr.city || addr.village || addr.suburb || '';
            
            setAddress({
              streetNumber,
              streetName,
              town,
              parish,
              lat: latitude,
              lng: longitude,
              verified: true
            });
            
            // Also update legacy state for compatibility
            const fullAddress = [streetNumber, streetName, town, parish].filter(Boolean).join(', ');
            setLegacy({ lat: latitude, lng: longitude, address: fullAddress || 'Location captured' });
            
            toast.success(`${type === 'pickup' ? 'Pickup' : 'Delivery'} location verified via GPS!`);
          } else {
            throw new Error('Geocode failed');
          }
        } catch (err) {
          // Fallback - just set coordinates
          setAddress(prev => ({
            ...prev,
            lat: latitude,
            lng: longitude,
            verified: true
          }));
          setLegacy({ lat: latitude, lng: longitude, address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` });
          toast.info("GPS captured. Please confirm address details.");
        }
        setGetting(false);
      },
      (err) => {
        if (err.code === 1) toast.error("Location permission denied. Please enable in browser settings.");
        else if (err.code === 2) toast.error("Location unavailable. Try again.");
        else toast.error("Location request timed out. Try again.");
        setGetting(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // Check if address is complete
  const isAddressComplete = (addr) => {
    return addr.streetNumber && addr.streetName && addr.town && addr.parish;
  };
  
  // Check if quote is ready for booking
  useEffect(() => {
    const pickupComplete = isAddressComplete(pickupAddress);
    const dropoffComplete = isAddressComplete(dropoffAddress) && dropoffAddress.verified;
    setQuoteReady(pickupComplete && dropoffComplete);
  }, [pickupAddress, dropoffAddress]);
  
  // Handle "Book Now" click - save quote data and redirect
  const handleBookNow = () => {
    // Save quote data to sessionStorage for the booking page
    const quoteData = {
      pickup: pickupAddress,
      dropoff: dropoffAddress,
      weight: priceWeight,
      distance: calculatedDistance || parseFloat(priceDistance),
      estimatedPrice: priceEstimate,
      timestamp: new Date().toISOString()
    };
    sessionStorage.setItem('pendingQuote', JSON.stringify(quoteData));
    setShowBookingPrompt(true);
  };
  
  // R29: booking is now public (guest gate at checkout), so always go straight to /send
  const proceedToBooking = (_requireAuth = false) => {
    navigate('/send');
  };

  // Search addresses for dropoff (uses Nominatim via our proxy)
  const searchDropoffAddress = async (query) => {
    if (!query || query.length < 3) {
      setDropoffSearchResults([]);
      return;
    }
    
    setSearchingDropoff(true);
    try {
      // Use Nominatim search with Jamaica bounds
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', Jamaica')}&countrycodes=jm&limit=5`
      );
      
      if (response.ok) {
        const results = await response.json();
        const formatted = results.map(r => ({
          address: r.display_name.replace(/, Jamaica$/, ''),
          lat: parseFloat(r.lat),
          lng: parseFloat(r.lon)
        }));
        setDropoffSearchResults(formatted);
        setShowDropoffSuggestions(formatted.length > 0);
      }
    } catch (err) {
      console.error('Address search error:', err);
    } finally {
      setSearchingDropoff(false);
    }
  };

  // Debounced search for dropoff
  useEffect(() => {
    const timer = setTimeout(() => {
      if (quoteDropoff.address && !quoteDropoff.lat) {
        searchDropoffAddress(quoteDropoff.address);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [quoteDropoff.address]);

  // Select a dropoff suggestion
  const selectDropoffSuggestion = (suggestion) => {
    setQuoteDropoff({
      lat: suggestion.lat,
      lng: suggestion.lng,
      address: suggestion.address
    });
    setShowDropoffSuggestions(false);
    setDropoffSearchResults([]);
    toast.success('Delivery location selected!');
  };
  
  // Search address and show on map for verification
  const searchAddressOnMap = async () => {
    // Build search query from entered address fields
    const parts = [
      dropoffAddress.streetNumber,
      dropoffAddress.streetName,
      dropoffAddress.town,
      dropoffAddress.parish
    ].filter(Boolean);
    
    if (parts.length < 2) {
      toast.error("Please enter at least street name and town to search");
      return;
    }
    
    const query = parts.join(', ') + ', Jamaica';
    setMapSearchQuery(query);
    setShowMapPicker(true);
    setSearchingMap(true);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=jm&limit=5&addressdetails=1`
      );
      
      if (response.ok) {
        const results = await response.json();
        const formatted = results.map(r => ({
          display: r.display_name.replace(/, Jamaica$/, ''),
          lat: parseFloat(r.lat),
          lng: parseFloat(r.lon),
          address: r.address || {}
        }));
        setMapSearchResults(formatted);
        
        if (formatted.length === 0) {
          toast.info("No results found. Try adjusting the address or pick location on map.");
        }
      }
    } catch (err) {
      console.error('Map search error:', err);
      toast.error("Search failed. You can still pick location on the map.");
    } finally {
      setSearchingMap(false);
    }
  };
  
  // Confirm location from map picker (from search results)
  const confirmMapLocation = async (lat, lng, addressStr = '') => {
    // Parse address string into components if available
    let parsedAddress = {};
    if (addressStr) {
      const parts = addressStr.split(',').map(p => p.trim());
      // Try to find parish from the address
      let parish = '';
      for (const p of PARISHES) {
        if (addressStr.includes(p)) {
          parish = p;
          break;
        }
      }
      parsedAddress = {
        streetName: parts[0] || '',
        town: parts[1] || parts[0] || '',
        parish
      };
    }
    
    setDropoffAddress(prev => ({
      ...prev,
      streetNumber: prev.streetNumber || parsedAddress.streetNumber || '',
      streetName: parsedAddress.streetName || prev.streetName || '',
      town: parsedAddress.town || prev.town || '',
      parish: parsedAddress.parish || prev.parish || '',
      lat,
      lng,
      verified: true
    }));
    setQuoteDropoff({ lat, lng, address: addressStr || `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
    setSelectedMapLocation({ lat, lng });
    setShowMapPicker(false);
    setMapSearchResults([]);
    toast.success('Delivery location verified on map!');
  };
  
  // Handle map click - select location and reverse geocode
  const handleMapClick = async (lat, lng) => {
    // Validate Jamaica bounds
    if (lat < 17.5 || lat > 18.7 || lng < -78.5 || lng > -76.0) {
      toast.error("Please select a location within Jamaica");
      return;
    }
    
    setSelectedMapLocation({ lat, lng });
    setReverseGeocodingMap(true);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      
      if (response.ok) {
        const data = await response.json();
        const addr = data.address || {};
        
        // Parse parish
        let parish = '';
        const parishFields = [addr.county, addr.state_district, addr.suburb, addr.city_district];
        for (const field of parishFields) {
          if (field) {
            const foundParish = PARISHES.find(p => 
              field.toLowerCase().includes(p.toLowerCase()) ||
              p.toLowerCase().includes(field.toLowerCase().replace('parish', '').trim())
            );
            if (foundParish) {
              parish = foundParish;
              break;
            }
          }
        }
        
        // Try from display_name if not found
        if (!parish && data.display_name) {
          for (const p of PARISHES) {
            if (data.display_name.includes(p)) {
              parish = p;
              break;
            }
          }
        }
        
        const streetNumber = addr.house_number || '';
        const streetName = addr.road || addr.street || '';
        const town = addr.town || addr.city || addr.village || addr.suburb || '';
        
        // Update address fields
        setDropoffAddress(prev => ({
          streetNumber: streetNumber || prev.streetNumber,
          streetName: streetName || prev.streetName,
          town: town || prev.town,
          parish: parish || prev.parish,
          lat,
          lng,
          verified: true
        }));
        
        const fullAddress = [streetNumber, streetName, town, parish].filter(Boolean).join(', ');
        setQuoteDropoff({ lat, lng, address: fullAddress || `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
        
        toast.success('Location selected! Address auto-filled.');
      } else {
        // Just set coordinates without address
        setDropoffAddress(prev => ({
          ...prev,
          lat,
          lng,
          verified: true
        }));
        setQuoteDropoff({ lat, lng, address: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
        toast.info('Location selected. Please confirm address details.');
      }
    } catch (err) {
      console.error('Reverse geocode error:', err);
      setDropoffAddress(prev => ({
        ...prev,
        lat,
        lng,
        verified: true
      }));
      setQuoteDropoff({ lat, lng, address: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
      toast.info('Location selected. Please confirm address details.');
    } finally {
      setReverseGeocodingMap(false);
    }
  };

  // Track a package by tracking number (public - no auth required)
  const handleTrackPackage = async () => {
    if (!trackingNumber.trim()) {
      toast.error("Please enter a tracking number");
      return;
    }
    setTrackingLoading(true);
    setTrackingResult(null);
    try {
      const response = await fetch(`/api/tracking/lookup/${trackingNumber.trim().toUpperCase()}`);
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.data) {
          setTrackingResult(data.data);
          setShowTrackingModal(true);
        } else {
          toast.error(data.error?.message || "Package not found");
        }
      } else if (response.status === 404) {
        toast.error("Package not found. Please check your tracking number.");
      } else {
        toast.error("Unable to track package. Please try again.");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setTrackingLoading(false);
    }
  };

  // Animate stats on load
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedStats(prev => ({
        deliveries: prev.deliveries < 1000 ? prev.deliveries + 50 : 1000,
        drivers: prev.drivers < 500 ? prev.drivers + 25 : 500,
        pudos: prev.pudos < 100 ? prev.pudos + 5 : 100
      }));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Calculate price
  useEffect(() => {
    let base = 500;
    const weight = parseInt(priceWeight);
    const distance = parseInt(priceDistance);
    if (weight <= 2) base += 100;
    else if (weight <= 5) base += 200;
    else if (weight <= 10) base += 350;
    else base += 500;
    if (distance <= 5) base += 200;
    else if (distance <= 10) base += 350;
    else if (distance <= 20) base += 500;
    else base += 700;
    setPriceEstimate(base);
  }, [priceWeight, priceDistance]);
  
  // Listen for map picker click events
  useEffect(() => {
    const onMapLocationSelected = (e) => {
      const { lat, lng } = e.detail;
      handleMapClick(lat, lng);
    };
    
    window.addEventListener('mapLocationSelected', onMapLocationSelected);
    return () => window.removeEventListener('mapLocationSelected', onMapLocationSelected);
  }, []);
  
  // Initialize Leaflet map when modal opens
  useEffect(() => {
    if (!showMapPicker) {
      // Cleanup when modal closes
      if (window._leafletMapPicker) {
        window._leafletMapPicker.remove();
        window._leafletMapPicker = null;
        window._leafletMarker = null;
      }
      return;
    }
    
    // Load Leaflet if not present
    const initLeafletMap = () => {
      const container = document.getElementById('map-picker-container');
      if (!container || container._leaflet_id) return;
      
      const L = window.L;
      if (!L) {
        setTimeout(initLeafletMap, 100);
        return;
      }
      
      // Initialize map centered on Jamaica
      const map = L.map(container).setView([18.1, -77.3], 9);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);
      
      let marker = null;
      
      // Handle map clicks
      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        
        // Remove existing marker
        if (marker) map.removeLayer(marker);
        
        // Add new marker
        marker = L.marker([lat, lng], {
          icon: L.divIcon({
            className: 'custom-marker',
            html: '<div style="background: #f59e0b; width: 24px; height: 24px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>',
            iconSize: [24, 24],
            iconAnchor: [12, 24]
          })
        }).addTo(map);
        
        window._leafletMarker = marker;
        
        // Dispatch event for React
        window.dispatchEvent(new CustomEvent('mapLocationSelected', {
          detail: { lat, lng }
        }));
      });
      
      window._leafletMapPicker = map;
      
      // If there's already a selected location, add marker
      if (selectedMapLocation) {
        marker = L.marker([selectedMapLocation.lat, selectedMapLocation.lng]).addTo(map);
        map.setView([selectedMapLocation.lat, selectedMapLocation.lng], 14);
        window._leafletMarker = marker;
      }
    };
    
    // Check if Leaflet is loaded
    if (!window.L) {
      // Load Leaflet CSS
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      
      // Load Leaflet JS
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setTimeout(initLeafletMap, 100);
        document.head.appendChild(script);
      } else {
        setTimeout(initLeafletMap, 100);
      }
    } else {
      setTimeout(initLeafletMap, 50);
    }
    
    return () => {
      if (window._leafletMapPicker) {
        window._leafletMapPicker.remove();
        window._leafletMapPicker = null;
        window._leafletMarker = null;
      }
    };
  }, [showMapPicker, selectedMapLocation]);

  const formatDistance = (km) => {
    if (km === null || km === undefined) return null;
    if (km < 1) return Math.round(km * 1000) + "m";
    return km.toFixed(1) + " km";
  };

  // Fetch PUDO locations from main site database
  const fetchPudoLocations = async (lat = null, lng = null) => {
    setPudoLoading(true);
    try {
      // Use the pilot API which syncs from main site
      const url = lat && lng 
        ? `/api/pudo/nearby?lat=${lat}&lng=${lng}&radius_km=50`
        : `/api/pudo/all`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch PUDO locations");
      
      const data = await response.json();
      let locations = data.nearby_pudos || data.pudos || data || [];
      
      // If we have user location, calculate distances and sort
      if (lat && lng && Array.isArray(locations)) {
        locations = locations.map(pudo => ({
          ...pudo,
          distance: calculateDistance(lat, lng, pudo.latitude, pudo.longitude)
        })).sort((a, b) => (a.distance || 999) - (b.distance || 999));
      }
      
      setPudoLocations(locations);
    } catch (err) {
      console.error("Error fetching PUDO locations:", err);
      // Fallback to sample data for pilot
      setPudoLocations([
        { id: "pudo-1", name: "Kingston Hub", address: "12 King Street, Kingston", parish: "Kingston", latitude: 18.0179, longitude: -76.8099, hours: "8AM-8PM", phone: "+1876-555-0101", rating: 4.8 },
        { id: "pudo-2", name: "Half Way Tree Centre", address: "45 Hope Road, Kingston", parish: "St. Andrew", latitude: 18.0108, longitude: -76.7868, hours: "7AM-9PM", phone: "+1876-555-0102", rating: 4.9 },
        { id: "pudo-3", name: "New Kingston Point", address: "28 Dominica Drive, Kingston", parish: "St. Andrew", latitude: 18.0068, longitude: -76.7839, hours: "8AM-7PM", phone: "+1876-555-0103", rating: 4.7 }
      ]);
    } finally {
      setPudoLoading(false);
    }
  };

  // Get user location
  const handleFindNearMe = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    setLocatingUser(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        fetchPudoLocations(latitude, longitude);
        setLocatingUser(false);
        toast.success("Found your location! Showing nearest PUDO centres.");
      },
      (error) => {
        setLocatingUser(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error("Location permission denied. Please enable location access.");
        } else {
          toast.error("Unable to get your location. Please try again.");
        }
        // Fetch all PUDOs without location
        fetchPudoLocations();
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // Open directions in Google Maps
  const handleDirections = (pudo) => {
    if (pudo.latitude && pudo.longitude) {
      const url = userLocation 
        ? `https://www.google.com/maps/dir/${userLocation.lat},${userLocation.lng}/${pudo.latitude},${pudo.longitude}`
        : `https://www.google.com/maps/dir/?api=1&destination=${pudo.latitude},${pudo.longitude}`;
      window.open(url, '_blank');
    }
  };

  // Filter PUDOs by search
  const filteredPudos = pudoLocations.filter(pudo => {
    if (!pudoSearchQuery) return true;
    const q = pudoSearchQuery.toLowerCase();
    return (pudo.name || "").toLowerCase().includes(q) ||
           (pudo.address || "").toLowerCase().includes(q) ||
           (pudo.parish || "").toLowerCase().includes(q);
  });

  const displayedPudos = showAllPudos ? filteredPudos : filteredPudos.slice(0, 3);

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* Tracking Modal */}
      {showTrackingModal && trackingResult && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowTrackingModal(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">Package Status</h3>
                <button onClick={() => setShowTrackingModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  trackingResult.status === 'delivered' ? 'bg-emerald-100 text-emerald-600' :
                  trackingResult.status === 'in_transit' ? 'bg-blue-100 text-blue-600' :
                  'bg-amber-100 text-amber-600'
                }`}>
                  <Package className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-bold text-slate-900">{trackingResult.tracking_id || trackingResult.tracking_code}</div>
                  <div className="text-sm text-slate-500 capitalize">{(trackingResult.status || "pending").replace(/_/g, " ")}</div>
                </div>
              </div>
              
              {trackingResult.timeline && trackingResult.timeline.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-700">Timeline</h4>
                  {trackingResult.timeline.map((event, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{event.status}</div>
                        <div className="text-xs text-slate-500">{new Date(event.timestamp).toLocaleString()}</div>
                        {event.location && <div className="text-xs text-slate-400">{event.location}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-2 gap-4 text-sm">
                {trackingResult.sender_name && (
                  <div>
                    <div className="text-slate-500">From</div>
                    <div className="font-medium text-slate-900">{trackingResult.sender_name}</div>
                  </div>
                )}
                {trackingResult.recipient_name && (
                  <div>
                    <div className="text-slate-500">To</div>
                    <div className="font-medium text-slate-900">{trackingResult.recipient_name}</div>
                  </div>
                )}
                {trackingResult.estimated_delivery && (
                  <div className="col-span-2">
                    <div className="text-slate-500">Estimated Delivery</div>
                    <div className="font-medium text-slate-900">{new Date(trackingResult.estimated_delivery).toLocaleDateString()}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 bg-slate-50">
              <Button onClick={() => setShowTrackingModal(false)} className={`w-full ${theme.btnPrimary}`}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* R30 Launch Promo Banner - fetches /api/promotions/active */}
      <LaunchPromoBanner />

      {/* Pilot Banner - Risk Disclosure */}
      <div className={`${theme.banner} py-2 sm:py-3 px-3 sm:px-4 text-center font-medium`}>
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
            <span className="font-bold text-sm sm:text-base">LIVE PILOT (30 Days)</span>
          </div>
          <span className="hidden sm:inline">|</span>
          <span className="text-emerald-100 text-xs sm:text-sm">
            Kingston & St. Andrew only • 
            <a href="mailto:support@liantiaoja.com" className="underline hover:text-white ml-1">Report issues</a>
          </span>
          <span className="hidden lg:inline">|</span>
          <span className="text-amber-300 text-xs sm:text-sm font-semibold hidden sm:inline">Data retained after pilot</span>
        </div>
      </div>

      {/* Header with Logo and Auth - Mobile Responsive */}
      <header className={`${theme.header} py-3 sm:py-4 px-4 sm:px-6 sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img 
              src={LOGO_PATH} 
              alt="Liantiao" 
              className="w-8 h-8 sm:w-12 sm:h-12 rounded-xl object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className={`text-lg sm:text-2xl font-black ${theme.logo}`}>Go <span className={theme.gold}>Liantiao</span></span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => document.getElementById('tracking-section').scrollIntoView({ behavior: 'smooth' })}
              className={`${theme.headerText} hover:opacity-70 font-medium transition-opacity flex items-center gap-1 text-sm sm:text-base`}
              data-testid="track-package-link"
            >
              <Eye className="w-4 h-4" /> <span className="hidden sm:inline">Track</span>
            </button>
            <Link to="/rides" className={`${theme.headerText} hover:opacity-70 font-medium transition-opacity flex items-center gap-1 text-sm sm:text-base text-blue-600`} data-testid="rides-link">
              <Car className="w-4 h-4" /> <span className="hidden sm:inline">Rides</span>
            </Link>
            <Link to="/pilot/ops" className={`hidden md:block ${theme.headerText} hover:opacity-70 font-medium transition-opacity text-sm sm:text-base`} data-testid="ops-portal-link">
              Ops Portal
            </Link>
            <Link to="/hub" className={`hidden md:flex ${theme.headerText} hover:opacity-70 font-medium transition-opacity items-center gap-1 text-sm sm:text-base`} data-testid="access-hub-link">
              Access Hub
            </Link>
            <Link to="/login" className={`${theme.headerText} hover:opacity-70 font-medium transition-opacity flex items-center gap-1 text-sm sm:text-base`} data-testid="login-link">
              <LogIn className="w-4 h-4" /> <span className="hidden sm:inline">Sign In</span>
            </Link>
            <Button
              onClick={() => navigate("/register")}
              className={`${theme.btnPrimary} text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2`}
              data-testid="signup-btn"
            >
              <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> <span className="hidden sm:inline">Sign Up</span><span className="sm:hidden">Join</span>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section with Tracking */}
      <section className={`${theme.hero} py-12 sm:py-16 md:py-24 px-4 sm:px-6 relative overflow-hidden`} id="tracking-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-48 sm:w-96 h-48 sm:h-96 bg-white opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-48 sm:w-96 h-48 sm:h-96 bg-white opacity-10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Hero Text */}
            <div className="text-center lg:text-left">
              <div className={`inline-flex items-center gap-2 ${theme.badge} px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold mb-4 sm:mb-8`}>
                <span className="w-2 h-2 bg-current rounded-full animate-pulse"></span>
                Liantiao Pilot — Now Accepting Applications
              </div>
              
              <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 leading-tight ${theme.heroText}`}>
                Jamaica's First
                <br />
                <span className={theme.heroAccent}>Delivery & Ride Platform</span>
              </h1>
              
              <p className={`text-base sm:text-lg md:text-xl ${theme.heroAccent} opacity-90 max-w-xl mx-auto lg:mx-0 mb-6 sm:mb-8 leading-relaxed`}>
                Book reliable rides or send packages across Jamaica. QR-verified deliveries, 
                real-time tracking, airport transfers, and tourist rides.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3 sm:gap-4 mb-6 sm:mb-8">
                <Button
                  onClick={() => navigate("/send")}
                  className={`${theme.btnPrimary} text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-xl w-full sm:w-auto`}
                  data-testid="hero-book-delivery-btn"
                >
                  Send a Package <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                </Button>
                <Button
                  onClick={() => navigate("/rides")}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-xl w-full sm:w-auto"
                  data-testid="hero-book-ride-btn"
                >
                  <Car className="mr-2 w-4 sm:w-5 h-4 sm:h-5" /> Book a Ride
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white/20 text-white border-2 border-white/50 hover:bg-white hover:text-slate-900 text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-xl backdrop-blur-sm w-full sm:w-auto"
                  data-testid="hero-get-quote-btn"
                >
                  Get a Quote
                </Button>
              </div>
            </div>

            {/* Right - Track Package Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${theme.goldBg} rounded-xl flex items-center justify-center`}>
                  <Search className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-900" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Track Your Package</h3>
                  <p className="text-white/70 text-xs sm:text-sm">Real-time tracking across Jamaica</p>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Enter tracking number..."
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleTrackPackage()}
                    className="h-12 sm:h-14 pl-4 sm:pl-5 pr-24 sm:pr-32 text-base sm:text-lg bg-white border-0 rounded-xl shadow-lg"
                    data-testid="tracking-input"
                  />
                  <Button
                    onClick={handleTrackPackage}
                    disabled={trackingLoading}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 ${theme.btnPrimary} px-6`}
                    data-testid="track-btn"
                  >
                    {trackingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                    {trackingLoading ? "..." : "Track"}
                  </Button>
                </div>
                
                <div className="grid grid-cols-3 gap-3 pt-2">
                  {[
                    { icon: Package, label: "Shop", desc: "Great deals" },
                    { icon: MapPinned, label: "Sell", desc: "List products" },
                    { icon: Truck, label: "Ship", desc: "Fast delivery" }
                  ].map((item, i) => (
                    <button
                      key={i}
                      className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-center transition-all"
                      onClick={() => i === 2 ? navigate("/login") : window.open("https://liantiaoja.com", "_blank")}
                    >
                      <item.icon className={`w-6 h-6 ${theme.heroAccent} mx-auto mb-2`} />
                      <div className="text-white text-sm font-semibold">{item.label}</div>
                      <div className="text-white/60 text-xs">{item.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Live Stats */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 mt-16 pt-8 border-t border-white/10">
            {[
              { value: animatedStats.deliveries, label: "Deliveries Daily", suffix: "+" },
              { value: animatedStats.drivers, label: "PPV Drivers", suffix: "+" },
              { value: animatedStats.pudos, label: "PUDO Locations", suffix: "+" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className={`text-4xl md:text-5xl font-black ${theme.heroAccent}`}>
                  {stat.value.toLocaleString()}{stat.suffix}
                </div>
                <div className={`${theme.heroText} opacity-70 text-sm font-medium`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Cards */}
      <section className={`py-24 px-6 ${theme.section}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-black ${theme.cardTitle} mb-4`}>
              Send, Ship, or Ride — Your Choice
            </h2>
            <p className={`text-xl ${theme.cardText}`}>Deliveries or rides, we have you covered across Jamaica</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">

            {/* Book a Ride - NEW */}
            <div className={`${theme.card} rounded-2xl p-6 lg:p-8 transition-all duration-300 ring-2 ring-offset-2 ring-blue-500 relative`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">NEW</span>
              </div>
              <div className="w-12 h-12 lg:w-16 lg:h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center mb-4 lg:mb-6">
                <Car className="w-6 h-6 lg:w-8 lg:h-8" />
              </div>
              <h3 className={`text-xl lg:text-2xl font-bold ${theme.cardTitle} mb-2 lg:mb-3`}>Book a Ride</h3>
              <p className={`${theme.cardText} mb-4 lg:mb-6 text-sm lg:text-base leading-relaxed`}>
                Get picked up anywhere in Jamaica. Airport transfers, tourist rides, and local trips.
              </p>
              <ul className="space-y-1.5 lg:space-y-2 mb-4 lg:mb-6">
                {["Airport pickups", "Tourist transfers (USD)", "Standard rides", "Real-time tracking"].map((f, i) => (
                  <li key={i} className={`flex items-center gap-2 text-xs lg:text-sm ${theme.cardText}`}>
                    <Check className="w-3 h-3 lg:w-4 lg:h-4 text-blue-500 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate("/rides")}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2.5 lg:py-3 text-sm lg:text-base font-semibold"
                data-testid="service-book-ride-btn"
              >
                Book a Ride <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {/* Standard Delivery */}
            <div className={`${theme.card} rounded-2xl p-8 transition-all duration-300`}>
              <div className={`w-16 h-16 ${theme.cardIcon} rounded-2xl flex items-center justify-center mb-6`}>
                <Package className="w-8 h-8" />
              </div>
              <h3 className={`text-2xl font-bold ${theme.cardTitle} mb-3`}>Standard Delivery</h3>
              <p className={`${theme.cardText} mb-6 leading-relaxed`}>
                Send packages up to 20kg anywhere in Kingston & St. Andrew with full tracking.
              </p>
              <ul className="space-y-2 mb-6">
                {["Real-time tracking", "QR code verification", "Insurance included", "Same-day available"].map((f, i) => (
                  <li key={i} className={`flex items-center gap-2 text-sm ${theme.cardText}`}>
                    <Check className={`w-4 h-4 ${theme.accent}`} /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/send")}
                className={`w-full ${theme.btnSecondary} py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all`}
                data-testid="standard-delivery-btn"
                type="button"
              >
                Send a Package <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Bulky Delivery */}
            <div className={`${theme.card} rounded-2xl p-8 transition-all duration-300 ring-2 ring-offset-2 ring-amber-400 relative`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className={`${theme.badge} px-3 py-1 rounded-full text-xs font-bold`}>POPULAR</span>
              </div>
              <div className={`w-16 h-16 ${theme.goldBg} text-slate-900 rounded-2xl flex items-center justify-center mb-6`}>
                <Sofa className="w-8 h-8" />
              </div>
              <h3 className={`text-2xl font-bold ${theme.cardTitle} mb-3`}>Bulky Item Delivery</h3>
              <p className={`${theme.cardText} mb-6 leading-relaxed`}>
                Move furniture, appliances, and large items with GPS-verified locations.
              </p>
              <ul className="space-y-2 mb-6">
                {["Furniture & appliances", "GPS location verification", "Instant price quotes", "Professional handling"].map((f, i) => (
                  <li key={i} className={`flex items-center gap-2 text-sm ${theme.cardText}`}>
                    <Check className={`w-4 h-4 ${theme.gold}`} /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => window.location.href = "/pilot/bulky.html"}
                className={`w-full ${theme.btnPrimary} py-3 font-semibold`}
                data-testid="bulky-delivery-btn"
              >
                Book Bulky Delivery <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Contract Hire */}
            <div className={`${theme.card} rounded-2xl p-8 transition-all duration-300`}>
              <div className={`w-16 h-16 ${theme.cardIcon} rounded-2xl flex items-center justify-center mb-6`}>
                <Truck className="w-8 h-8" />
              </div>
              <h3 className={`text-2xl font-bold ${theme.cardTitle} mb-3`}>Contract a Vehicle</h3>
              <p className={`${theme.cardText} mb-6 leading-relaxed`}>
                Hire a van or truck with driver by the day for moves or business deliveries.
              </p>
              <ul className="space-y-2 mb-6">
                {["Van & truck options", "Full day or half day", "Choose your service area", "Dedicated driver"].map((f, i) => (
                  <li key={i} className={`flex items-center gap-2 text-sm ${theme.cardText}`}>
                    <Check className={`w-4 h-4 ${theme.accent}`} /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => window.location.href = "/pilot/hire.html"}
                className={`w-full ${theme.btnSecondary} py-3 font-semibold`}
                data-testid="contract-hire-btn"
              >
                Book Contract Hire <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Price Calculator */}
      <section id="calculator" className={`py-12 sm:py-24 px-4 sm:px-6 ${theme.sectionAlt}`}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white shadow-2xl rounded-2xl p-6 sm:p-10">
            <h3 className={`text-2xl sm:text-3xl font-black ${theme.cardTitle} mb-2 flex items-center gap-3`}>
              <Zap className={`w-6 sm:w-8 h-6 sm:h-8 ${theme.accent}`} />
              Quick Price Estimate
            </h3>
            <p className={`${theme.cardText} mb-6 sm:mb-8 text-sm sm:text-base`}>
              Get an instant quote and book your delivery in minutes
            </p>
            
            {/* PICKUP ADDRESS (Sender) */}
            <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-bold text-emerald-800 flex items-center gap-2">
                  <LocateFixed className="w-4 h-4" /> Pickup Address (Sender)
                </label>
                <Button
                  onClick={() => getStructuredGPS('pickup')}
                  disabled={gettingPickupGPS}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1"
                >
                  {gettingPickupGPS ? <Loader2 className="w-3 h-3 animate-spin" /> : '📍 Use GPS'}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <input
                    type="text"
                    placeholder="Street #"
                    value={pickupAddress.streetNumber}
                    onChange={(e) => setPickupAddress(p => ({...p, streetNumber: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-emerald-500"
                    data-testid="pickup-street-number"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Street Name *"
                    value={pickupAddress.streetName}
                    onChange={(e) => setPickupAddress(p => ({...p, streetName: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-emerald-500"
                    data-testid="pickup-street-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    placeholder="Town/City *"
                    value={pickupAddress.town}
                    onChange={(e) => setPickupAddress(p => ({...p, town: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-emerald-500"
                    data-testid="pickup-town"
                  />
                </div>
                <div>
                  <select
                    value={pickupAddress.parish}
                    onChange={(e) => setPickupAddress(p => ({...p, parish: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-emerald-500"
                    data-testid="pickup-parish"
                  >
                    <option value="">Parish *</option>
                    {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              {pickupAddress.lat && (
                <div className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                  <Check className="w-3 h-3" /> GPS verified
                </div>
              )}
            </div>
            
            {/* DROPOFF ADDRESS (Receiver) - Requires GPS Verification */}
            <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-bold text-amber-800 flex items-center gap-2">
                  <MapPinned className="w-4 h-4" /> Delivery Address (Receiver)
                </label>
                <Button
                  onClick={searchAddressOnMap}
                  disabled={gettingDropoffGPS || searchingMap}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1"
                  data-testid="find-on-map-btn"
                >
                  {searchingMap ? <Loader2 className="w-3 h-3 animate-spin" /> : <Map className="w-3 h-3 mr-1" />}
                  {searchingMap ? 'Searching...' : 'Find on Map'}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <input
                    type="text"
                    placeholder="Street #"
                    value={dropoffAddress.streetNumber}
                    onChange={(e) => setDropoffAddress(p => ({...p, streetNumber: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-amber-500"
                    data-testid="dropoff-street-number"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Street Name *"
                    value={dropoffAddress.streetName}
                    onChange={(e) => setDropoffAddress(p => ({...p, streetName: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-amber-500"
                    data-testid="dropoff-street-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    placeholder="Town/City *"
                    value={dropoffAddress.town}
                    onChange={(e) => setDropoffAddress(p => ({...p, town: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-amber-500"
                    data-testid="dropoff-town"
                  />
                </div>
                <div>
                  <select
                    value={dropoffAddress.parish}
                    onChange={(e) => setDropoffAddress(p => ({...p, parish: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-amber-500"
                    data-testid="dropoff-parish"
                  >
                    <option value="">Parish *</option>
                    {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              {dropoffAddress.verified ? (
                <div className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                  <Check className="w-3 h-3" /> GPS verified - Location confirmed
                </div>
              ) : (
                <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                  ⚠️ GPS verification required for receiver location
                </div>
              )}
            </div>
            
            {/* Mini-Map Preview */}
            {(pickupAddress.lat || dropoffAddress.lat) && (
              <div className="mb-4 rounded-xl overflow-hidden border border-slate-200">
                <div className="bg-slate-100 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
                  <Map className="w-4 h-4 text-slate-600" />
                  <span className="text-xs font-medium text-slate-600">Route Preview</span>
                </div>
                <div className="relative h-32 bg-slate-50">
                  <img 
                    src={`https://staticmap.openstreetmap.de/staticmap.php?center=${
                      pickupAddress.lat && dropoffAddress.lat 
                        ? `${(pickupAddress.lat + dropoffAddress.lat) / 2},${(pickupAddress.lng + dropoffAddress.lng) / 2}`
                        : pickupAddress.lat 
                          ? `${pickupAddress.lat},${pickupAddress.lng}`
                          : `${dropoffAddress.lat},${dropoffAddress.lng}`
                    }&zoom=${pickupAddress.lat && dropoffAddress.lat ? 11 : 14}&size=600x128&maptype=osmarenderer${
                      pickupAddress.lat ? `&markers=${pickupAddress.lat},${pickupAddress.lng},ol-marker-green` : ''
                    }${
                      dropoffAddress.lat ? `&markers=${dropoffAddress.lat},${dropoffAddress.lng},ol-marker` : ''
                    }`}
                    alt="Route Map"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="bg-white px-3 py-2 flex items-center justify-between text-xs">
                  <div className="flex gap-4">
                    {pickupAddress.lat && (
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Pickup
                      </span>
                    )}
                    {dropoffAddress.lat && (
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span> Delivery
                      </span>
                    )}
                  </div>
                  {calculatedDistance && (
                    <span className="text-emerald-600 font-bold">{calculatedDistance.toFixed(1)} km</span>
                  )}
                </div>
              </div>
            )}
            
            {/* Weight & Distance */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className={`text-sm font-medium ${theme.cardText} block mb-2`}>Package Weight</label>
                <select
                  value={priceWeight}
                  onChange={(e) => setPriceWeight(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="weight-select"
                >
                  <option value="2">Up to 2 kg</option>
                  <option value="5">2 - 5 kg</option>
                  <option value="10">5 - 10 kg</option>
                  <option value="20">10 - 20 kg</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${theme.cardText} block mb-2`}>Distance</label>
                <select
                  value={priceDistance}
                  onChange={(e) => setPriceDistance(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="distance-select"
                >
                  <option value="5">Within 5 km</option>
                  <option value="10">5 - 10 km</option>
                  <option value="20">10 - 20 km</option>
                  <option value="30">20+ km</option>
                </select>
              </div>
            </div>
            
            {/* Price Display */}
            <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 rounded-2xl p-6 text-center mb-6">
              <div className="text-white text-sm font-medium mb-2 opacity-90">Estimated Price</div>
              <div className="text-4xl sm:text-5xl font-black text-amber-400" data-testid="price-estimate">
                <span className="text-xl sm:text-2xl">JMD</span> {priceEstimate.toLocaleString()}
              </div>
            </div>
            
            {/* Book Now CTA */}
            {quoteReady ? (
              <Button
                onClick={handleBookNow}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-4 rounded-xl text-lg shadow-lg"
                data-testid="book-now-btn"
              >
                Book This Delivery Now <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <div className="text-center py-3">
                <p className="text-sm text-slate-500">
                  {!isAddressComplete(pickupAddress) && "Complete pickup address • "}
                  {!isAddressComplete(dropoffAddress) && "Complete delivery address • "}
                  {isAddressComplete(dropoffAddress) && !dropoffAddress.verified && "Verify delivery GPS"}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Booking Prompt Modal */}
      {showBookingPrompt && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Ready to Book!</h3>
              <p className="text-slate-600">Your quote is saved. Sign in to complete your booking.</p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">From:</span>
                <span className="text-slate-900 font-medium">{pickupAddress.town}, {pickupAddress.parish}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">To:</span>
                <span className="text-slate-900 font-medium">{dropoffAddress.town}, {dropoffAddress.parish}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
                <span className="text-slate-600">Est. Price:</span>
                <span className="text-emerald-600 font-bold">JMD {priceEstimate.toLocaleString()}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => proceedToBooking(true)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl"
              >
                <LogIn className="w-4 h-4 mr-2" /> Sign In to Book
              </Button>
              <Button
                onClick={() => navigate('/register?redirect=/book-package')}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 rounded-xl"
              >
                <User className="w-4 h-4 mr-2" /> Create Account
              </Button>
              <button
                onClick={() => setShowBookingPrompt(false)}
                className="w-full text-sm text-slate-500 hover:text-slate-700 py-2"
              >
                Back to Quote
              </button>
            </div>
            
            <p className="text-xs text-center text-slate-400 mt-4">
              Your quote will be saved and auto-filled when you sign in
            </p>
          </div>
        </div>
      )}
      
      {/* Map Picker Modal for Receiver Location */}
      {showMapPicker && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Find Delivery Location</h3>
                <p className="text-sm text-slate-500">Search or click on the map to set location</p>
              </div>
              <button
                onClick={() => setShowMapPicker(false)}
                className="text-slate-400 hover:text-slate-600 text-xl"
              >
                ✕
              </button>
            </div>
            
            {/* Search Bar in Modal */}
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={mapSearchQuery}
                  onChange={(e) => setMapSearchQuery(e.target.value)}
                  placeholder="Search address in Jamaica..."
                  className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="map-search-input"
                />
                <Button
                  onClick={async () => {
                    if (!mapSearchQuery.trim()) return;
                    setSearchingMap(true);
                    try {
                      const response = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery + ', Jamaica')}&countrycodes=jm&limit=5&addressdetails=1`
                      );
                      if (response.ok) {
                        const results = await response.json();
                        setMapSearchResults(results.map(r => ({
                          display: r.display_name.replace(/, Jamaica$/, ''),
                          lat: parseFloat(r.lat),
                          lng: parseFloat(r.lon),
                          address: r.address || {}
                        })));
                      }
                    } catch (err) {
                      toast.error("Search failed");
                    } finally {
                      setSearchingMap(false);
                    }
                  }}
                  disabled={searchingMap}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-4"
                  data-testid="map-search-btn"
                >
                  {searchingMap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            {/* Search Results */}
            {mapSearchResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto border-b border-slate-200">
                {mapSearchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => confirmMapLocation(result.lat, result.lng, result.display)}
                    className="w-full text-left px-4 py-3 hover:bg-amber-50 border-b border-slate-100 last:border-0"
                    data-testid={`map-result-${idx}`}
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{result.display.split(',')[0]}</div>
                        <div className="text-xs text-slate-500">{result.display}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* Interactive Clickable Map */}
            <div className="relative h-72 sm:h-96 bg-slate-100">
              <div 
                id="map-picker-container"
                className="w-full h-full"
                style={{ zIndex: 1 }}
              />
              
              {/* Loading overlay for reverse geocoding */}
              {reverseGeocodingMap && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Getting address...</span>
                  </div>
                </div>
              )}
              
              <div className="absolute bottom-2 left-2 right-2 bg-white/95 backdrop-blur rounded-lg p-3 text-sm text-slate-700 shadow-lg z-20">
                <div className="flex items-center gap-2">
                  <MapPinned className="w-4 h-4 text-amber-500" />
                  <strong>Click on the map</strong> to select the delivery location, or search above.
                </div>
              </div>
            </div>
            
            {/* Selected Location Display */}
            {dropoffAddress.lat && dropoffAddress.verified && (
              <div className="p-3 bg-emerald-50 border-t border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-700 text-sm">
                  <Check className="w-4 h-4" />
                  <span>Location verified: {dropoffAddress.lat.toFixed(5)}, {dropoffAddress.lng.toFixed(5)}</span>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="p-4 border-t border-slate-200 flex gap-3">
              <Button
                onClick={() => setShowMapPicker(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (dropoffAddress.verified && dropoffAddress.lat) {
                    setShowMapPicker(false);
                    toast.success('Location confirmed!');
                  } else {
                    toast.error('Please select a location from search results');
                  }
                }}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={!dropoffAddress.verified}
              >
                <Check className="w-4 h-4 mr-2" /> Confirm Location
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PUDO Finder Section */}
      <section id="pudo-finder" className={`py-24 px-6 ${theme.section}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${theme.badge} px-5 py-2 rounded-full text-sm font-bold mb-6`}>
              <MapPin className="w-4 h-4" /> PUDO Network
            </div>
            <h2 className={`text-4xl md:text-5xl font-black ${theme.cardTitle} mb-4`}>
              Find a <span className={theme.gold}>PUDO Centre</span>
            </h2>
            <p className={`text-xl ${theme.cardText}`}>Pickup and drop-off locations across Kingston & St. Andrew</p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-10">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input 
                type="text" 
                placeholder="Search by location, parish, or address..." 
                value={pudoSearchQuery} 
                onChange={(e) => setPudoSearchQuery(e.target.value)} 
                className="pl-12 pr-36 h-14 text-lg bg-white border-slate-300 focus:border-emerald-500 rounded-2xl shadow-sm" 
                data-testid="pudo-search-input"
              />
              <Button 
                className={`absolute right-2 top-1/2 -translate-y-1/2 ${theme.btnPrimary}`}
                onClick={handleFindNearMe}
                disabled={locatingUser}
                data-testid="find-near-me-btn"
              >
                {locatingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-2" />}
                {locatingUser ? "Locating..." : "Near Me"}
              </Button>
            </div>
            
            {userLocation && (
              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-emerald-600">
                <LocateFixed className="w-4 h-4" />
                <span>Showing nearest locations to you</span>
                <button 
                  onClick={() => { setUserLocation(null); setPudoLocations([]); }}
                  className="text-slate-500 hover:text-slate-700 underline ml-2"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* PUDO Cards */}
          {pudoLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              <span className="ml-3 text-slate-500">Finding PUDO locations...</span>
            </div>
          ) : pudoLocations.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className={`${theme.cardText} mb-6`}>Click "Near Me" to find PUDO centres near you</p>
              <Button onClick={handleFindNearMe} className={theme.btnSecondary} data-testid="find-pudo-cta">
                <LocateFixed className="w-4 h-4 mr-2" /> Find Nearest PUDO
              </Button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {displayedPudos.map((pudo, index) => (
                  <Card key={pudo.id} className={`${theme.card} overflow-hidden`} data-testid={`pudo-card-${index}`}>
                    {userLocation && index === 0 && pudo.distance && (
                      <div className="absolute top-3 right-3 z-10">
                        <Badge className="bg-emerald-600 text-white">Nearest</Badge>
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 pr-2">
                          <h3 className={`font-semibold ${theme.cardTitle} text-lg`}>{pudo.name}</h3>
                          <p className={`text-sm ${theme.cardText} mt-1`}>{pudo.address}</p>
                        </div>
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">{pudo.parish}</Badge>
                      </div>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center gap-3 text-sm">
                          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Clock className="w-4 h-4 text-emerald-600" />
                          </div>
                          <span className={theme.cardText}>{pudo.hours || "8AM - 8PM"}</span>
                        </div>
                        
                        {pudo.distance !== undefined && pudo.distance !== null && (
                          <div className="flex items-center gap-3 text-sm">
                            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                              <Navigation className="w-4 h-4 text-amber-600" />
                            </div>
                            <span className="text-amber-600 font-medium">{formatDistance(pudo.distance)} away</span>
                          </div>
                        )}
                        
                        {pudo.rating && (
                          <div className="flex items-center gap-3 text-sm">
                            <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            </div>
                            <span className={theme.cardText}>{pudo.rating} rating</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-3 mt-auto">
                        {pudo.phone && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => window.open(`tel:${pudo.phone}`, '_self')}
                            data-testid={`call-pudo-${index}`}
                          >
                            <Phone className="w-4 h-4 mr-1" /> Call
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          className={`flex-1 ${theme.btnPrimary}`}
                          onClick={() => handleDirections(pudo)}
                          data-testid={`directions-pudo-${index}`}
                        >
                          Directions <ExternalLink className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {filteredPudos.length > 3 && (
                <div className="text-center">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAllPudos(!showAllPudos)}
                    className="group"
                    data-testid="show-all-pudos-btn"
                  >
                    {showAllPudos ? "Show Less" : `View All ${filteredPudos.length} PUDO Centres`}
                    <ChevronRight className={`w-5 h-5 transition-transform ${showAllPudos ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className={`py-24 px-6 ${theme.sectionAlt}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-black ${theme.cardTitle} mb-4`}>How It Works</h2>
            <p className={`text-xl ${theme.cardText}`}>Simple, secure, and transparent delivery</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { num: 1, title: "Request Quote", desc: "Enter your pickup and delivery details for an instant price", icon: Package },
              { num: 2, title: "Secure Payment", desc: "Pay online via Stripe with full transaction protection", icon: CreditCard },
              { num: 3, title: "Driver Pickup", desc: "A verified driver collects your item with QR confirmation", icon: QrCode },
              { num: 4, title: "Track & Receive", desc: "Track in real-time and confirm delivery with PIN code", icon: MapPin }
            ].map((step, i) => (
              <div key={i} className="text-center group">
                <div className={`w-20 h-20 ${theme.card} rounded-full flex items-center justify-center mx-auto mb-5 text-2xl font-black ${theme.accent} group-hover:scale-110 transition-transform`}>
                  {step.num}
                </div>
                <h4 className={`text-xl font-bold ${theme.cardTitle} mb-2`}>{step.title}</h4>
                <p className={`text-sm ${theme.cardText}`}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Network */}
      <section className={`py-24 px-6 ${theme.section}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-black ${theme.cardTitle} mb-4`}>Join the Liantiao Network</h2>
            <p className={`text-xl ${theme.cardText}`}>Be part of Jamaica's delivery revolution</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Driver Card */}
            <div className={`${theme.card} rounded-2xl p-10 flex flex-col md:flex-row items-center gap-8`}>
              <div className={`w-24 h-24 ${theme.accentBg} rounded-2xl flex items-center justify-center text-5xl flex-shrink-0 text-white`}>
                🚗
              </div>
              <div>
                <h3 className={`text-2xl font-bold ${theme.cardTitle} mb-3`}>Become a Liantiao Driver</h3>
                <p className={`${theme.cardText} mb-5 leading-relaxed`}>
                  Join as one of our first drivers. Get priority assignments and earn competitive rates.
                </p>
                <Button
                  onClick={() => window.location.href = "https://liantiaoja.com/dashboard/apply/driver"}
                  className={theme.btnSecondary}
                  data-testid="apply-driver-btn"
                >
                  Apply Now <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            
            {/* PUDO Card */}
            <div className={`${theme.card} rounded-2xl p-10 flex flex-col md:flex-row items-center gap-8`}>
              <div className={`w-24 h-24 ${theme.goldBg} rounded-2xl flex items-center justify-center text-5xl flex-shrink-0 text-slate-900`}>
                📦
              </div>
              <div>
                <h3 className={`text-2xl font-bold ${theme.cardTitle} mb-3`}>Partner as PUDO Centre</h3>
                <p className={`${theme.cardText} mb-5 leading-relaxed`}>
                  Turn your business into a package hub. Accept drop-offs and earn per-package fees.
                </p>
                <Button
                  onClick={() => window.location.href = "https://liantiaoja.com/dashboard/apply/pudo"}
                  className={theme.btnPrimary}
                  data-testid="apply-pudo-btn"
                >
                  Partner With Us <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className={`py-16 px-6 ${theme.hero}`}>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: QrCode, title: "QR Verification", desc: "Every handoff confirmed with secure QR codes" },
            { icon: MapPin, title: "Live Tracking", desc: "Know exactly where your package is at all times" },
            { icon: CreditCard, title: "Secure Payments", desc: "Stripe-powered transactions with full protection" },
            { icon: Shield, title: "Pilot Monitored", desc: "All deliveries supervised during testing phase" }
          ].map((item, i) => (
            <div key={i} className="text-center">
              <item.icon className={`w-10 h-10 ${theme.heroAccent} mx-auto mb-4`} />
              <h4 className={`${theme.heroText} font-bold mb-2`}>{item.title}</h4>
              <p className={`text-sm ${theme.heroText} opacity-70`}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Coverage + Find PUDO */}
      <section className={`py-24 px-6 ${theme.section} text-center`}>
        <div className="max-w-xl mx-auto">
          <div className={`inline-flex items-center gap-2 ${theme.badge} px-5 py-2 rounded-full text-sm font-bold mb-6`}>
            <MapPin className="w-4 h-4" /> Service Area
          </div>
          <h3 className={`text-4xl font-black ${theme.cardTitle} mb-3`}>Currently Serving</h3>
          <div className={`text-3xl font-bold ${theme.accent} mb-3`}>Kingston & St. Andrew</div>
          <p className={`${theme.cardText} mb-8`}>Coming soon: St. Catherine, Clarendon</p>
          <Button 
            onClick={() => document.getElementById('pudo-finder').scrollIntoView({ behavior: 'smooth' })}
            className={`${theme.btnSecondary} px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:scale-105`}
            data-testid="find-pudo-footer-btn"
          >
            <Navigation className="w-5 h-5 mr-2" /> Find Closest PUDO
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className={`${theme.footer} py-12 px-6 text-center`}>
        <p className="opacity-70 mb-4">© 2026 Liantiao — Building Jamaica's Delivery Chain</p>
        <p className="opacity-50">
          Need help? <a href="mailto:support@liantiaoja.com" className={`${theme.gold} hover:underline`}>support@liantiaoja.com</a>
        </p>
      </footer>
    </div>
  );
};

export default ModernWelcome;
